/* geometry.js — the arithmetic behind the Crop / Split photo tool.
 *
 * Everything here is pure: numbers and typed arrays in, numbers out. No DOM, no
 * canvas. That is deliberate — the parts that are easy to get subtly wrong
 * (remainder distribution when a width does not divide evenly, the order in
 * which Instagram tiles must be uploaded, how many sheets a poster needs once
 * printer margins and glue overlap are accounted for) are exactly the parts
 * worth unit-testing on real numbers instead of eyeballing in a browser.
 *
 * CommonJS-exported for tests/js/photo-crop-geometry.test.mjs.
 */
(function (root, factory) {
    'use strict';
    var mod = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = mod;
    if (typeof window !== 'undefined') window.PhotoCropGeometry = mod;
    // a worker has no window: the /gif-trim worker measures borders with this module
    else if (root) root.PhotoCropGeometry = mod;
})(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    var MM_PER_INCH = 25.4;

    function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }
    function round(v) { return Math.round(v); }

    /* ════════════════════════════════════════════════════════════════
       ASPECT-RATIO CROP RECTANGLES
       ════════════════════════════════════════════════════════════════ */

    /** Largest rect of the given aspect that fits in w x h, centred. */
    function largestRectOfAspect(w, h, aspect) {
        if (!aspect || aspect <= 0) return { x: 0, y: 0, w: w, h: h };
        var rw = w, rh = w / aspect;
        if (rh > h) { rh = h; rw = h * aspect; }
        rw = Math.min(w, Math.floor(rw));
        rh = Math.min(h, Math.floor(rh));
        return { x: Math.floor((w - rw) / 2), y: Math.floor((h - rh) / 2), w: rw, h: rh };
    }

    /**
     * Keep a rect inside the image while preserving its aspect ratio.
     * Used while dragging: the naive approach (clamp x, y, w, h independently)
     * silently changes the ratio at the edges, which is why locked-ratio crops
     * in many editors "go slightly off" when you push them into a corner.
     */
    function constrainRect(rect, imgW, imgH, aspect, minSize) {
        var min = Math.max(1, minSize || 1);
        var w = rect.w, h = rect.h;
        if (aspect && aspect > 0) {
            // fit to the ratio first
            if (w / h > aspect) w = h * aspect; else h = w / aspect;
            // then grow (never clamp — clamping one side is what silently
            // breaks the locked ratio) until BOTH sides clear the minimum
            if (w < min) { w = min; h = w / aspect; }
            if (h < min) { h = min; w = h * aspect; }
            // then shrink to fit the image, again proportionally
            if (w > imgW) { w = imgW; h = w / aspect; }
            if (h > imgH) { h = imgH; w = h * aspect; }
            w = Math.max(1, Math.floor(w));
            h = Math.max(1, Math.floor(h));
        } else {
            w = Math.min(Math.max(min, w), imgW);
            h = Math.min(Math.max(min, h), imgH);
            w = Math.max(1, Math.floor(w));
            h = Math.max(1, Math.floor(h));
        }
        var x = clamp(Math.round(rect.x), 0, Math.max(0, imgW - w));
        var y = clamp(Math.round(rect.y), 0, Math.max(0, imgH - h));
        return { x: x, y: y, w: w, h: h };
    }

    /* ════════════════════════════════════════════════════════════════
       AUTO-TRIM — remove a uniform border
       ════════════════════════════════════════════════════════════════ */

    /**
     * Find the content box inside a uniform frame: scanner white margins,
     * letterbox bars on a screenshot, the flat backdrop of a product photo.
     *
     * @param data   RGBA bytes, w*h*4
     * @param tol    per-channel tolerance (0-255). 0 means "exactly equal".
     * @param opts   {edges: {left,right,top,bottom}} to restrict which sides trim
     * @returns {x,y,w,h,trimmed,color:[r,g,b]}
     *
     * The border colour is taken as the median of the four corners, so a single
     * dead pixel or a JPEG artefact in one corner cannot decide the whole crop.
     */
    function detectBorders(data, w, h, tol, opts) {
        tol = tol == null ? 12 : tol;
        opts = opts || {};
        var edges = opts.edges || { left: 1, right: 1, top: 1, bottom: 1 };
        if (w < 2 || h < 2) return { x: 0, y: 0, w: w, h: h, trimmed: false, color: [0, 0, 0] };

        var corners = [
            [0, 0], [w - 1, 0], [0, h - 1], [w - 1, h - 1]
        ].map(function (p) {
            var i = (p[1] * w + p[0]) * 4;
            return [data[i], data[i + 1], data[i + 2]];
        });
        var color = [0, 1, 2].map(function (ch) {
            var vals = corners.map(function (c) { return c[ch]; }).sort(function (a, b) { return a - b; });
            return Math.round((vals[1] + vals[2]) / 2);       // median of four
        });

        // A handful of stray pixels must not veto a whole border line. Real
        // scans have dust, sensor hot pixels and JPEG ringing on the very edge;
        // demanding a perfectly uniform row means the trim simply never fires
        // on exactly the photos it was built for.
        function allowedOutliers(len) {
            return Math.max(2, Math.ceil(len * (opts.outlierFrac == null ? 0.005 : opts.outlierFrac)));
        }
        function differs(i) {
            if (data[i + 3] === 0) return false;             // transparent counts as border
            return Math.abs(data[i] - color[0]) > tol ||
                   Math.abs(data[i + 1] - color[1]) > tol ||
                   Math.abs(data[i + 2] - color[2]) > tol;
        }
        function rowIsBorder(y) {
            var bad = 0, allow = allowedOutliers(w);
            for (var x = 0; x < w; x++) {
                if (differs((y * w + x) * 4) && ++bad > allow) return false;
            }
            return true;
        }
        function colIsBorder(x) {
            var bad = 0, allow = allowedOutliers(h);
            for (var y = 0; y < h; y++) {
                if (differs((y * w + x) * 4) && ++bad > allow) return false;
            }
            return true;
        }

        var top = 0, bottom = h - 1, left = 0, right = w - 1;
        if (edges.top) while (top < bottom && rowIsBorder(top)) top++;
        if (edges.bottom) while (bottom > top && rowIsBorder(bottom)) bottom--;
        if (edges.left) while (left < right && colIsBorder(left)) left++;
        if (edges.right) while (right > left && colIsBorder(right)) right--;

        var box = { x: left, y: top, w: right - left + 1, h: bottom - top + 1 };
        box.trimmed = (box.x !== 0 || box.y !== 0 || box.w !== w || box.h !== h);
        box.color = color;
        return box;
    }

    /* ════════════════════════════════════════════════════════════════
       SMART CROP — pick the most interesting window of a given aspect
       ════════════════════════════════════════════════════════════════ */

    /**
     * Cheap saliency: edge energy (Sobel on luma) plus a colourfulness term, on
     * a downscaled grid. Detail and saturation are what the eye lands on; a
     * plain "centre crop" throws away the subject whenever it is off-centre.
     *
     * @returns {map:Float32Array(gw*gh), gw, gh, sx, sy}  sx/sy = image px per cell
     */
    function saliencyMap(data, w, h, gridTarget) {
        var target = gridTarget || 64;
        var gw = Math.max(4, Math.min(target, w));
        var gh = Math.max(4, Math.min(target, h));
        var sx = w / gw, sy = h / gh;

        // average luma + saturation per cell
        var luma = new Float32Array(gw * gh);
        var sat = new Float32Array(gw * gh);
        var cnt = new Float32Array(gw * gh);
        for (var y = 0; y < h; y++) {
            var gy = Math.min(gh - 1, (y / sy) | 0);
            for (var x = 0; x < w; x++) {
                var i = (y * w + x) * 4;
                var g = Math.min(gw - 1, (x / sx) | 0), gi = gy * gw + g;
                var r = data[i], gg = data[i + 1], b = data[i + 2];
                luma[gi] += 0.299 * r + 0.587 * gg + 0.114 * b;
                var mx = Math.max(r, gg, b), mn = Math.min(r, gg, b);
                sat[gi] += mx === 0 ? 0 : (mx - mn) / mx;
                cnt[gi]++;
            }
        }
        for (var k = 0; k < luma.length; k++) {
            if (cnt[k]) { luma[k] /= cnt[k]; sat[k] /= cnt[k]; }
        }

        var map = new Float32Array(gw * gh);
        for (var cy = 0; cy < gh; cy++) {
            for (var cx = 0; cx < gw; cx++) {
                var c = cy * gw + cx;
                var l = clamp(cx - 1, 0, gw - 1), rr = clamp(cx + 1, 0, gw - 1);
                var u = clamp(cy - 1, 0, gh - 1), d = clamp(cy + 1, 0, gh - 1);
                var dx = luma[cy * gw + rr] - luma[cy * gw + l];
                var dy = luma[d * gw + cx] - luma[u * gw + cx];
                var edge = Math.sqrt(dx * dx + dy * dy) / 255;
                map[c] = edge + 0.35 * sat[c];
            }
        }
        return { map: map, gw: gw, gh: gh, sx: sx, sy: sy };
    }

    /**
     * Slide a window of the requested aspect over the saliency map and keep the
     * best-scoring position. Score = summed energy, mildly biased towards the
     * centre and towards putting energy near the rule-of-thirds lines.
     *
     * @param boost  optional [{x,y,w,h,weight}] in IMAGE pixels — e.g. detected
     *               faces. Callers that have a face detector can hand it in;
     *               the engine stays dependency-free without one.
     */
    function bestCrop(sal, imgW, imgH, aspect, opts) {
        opts = opts || {};
        var boost = opts.boost || [];
        var base = largestRectOfAspect(imgW, imgH, aspect);
        var cw = base.w, ch = base.h;
        if (cw >= imgW && ch >= imgH) return { x: 0, y: 0, w: imgW, h: imgH, score: 0 };

        var gw = sal.gw, gh = sal.gh, map = sal.map;
        // integral image for O(1) window sums
        var integ = new Float64Array((gw + 1) * (gh + 1));
        for (var y = 0; y < gh; y++) {
            var rowSum = 0;
            for (var x = 0; x < gw; x++) {
                rowSum += map[y * gw + x];
                integ[(y + 1) * (gw + 1) + (x + 1)] = integ[y * (gw + 1) + (x + 1)] + rowSum;
            }
        }
        function areaSum(x0, y0, x1, y1) {   // inclusive-exclusive, grid coords
            var W = gw + 1;
            return integ[y1 * W + x1] - integ[y0 * W + x1] - integ[y1 * W + x0] + integ[y0 * W + x0];
        }

        var stepX = Math.max(1, Math.round((imgW - cw) / 40));
        var stepY = Math.max(1, Math.round((imgH - ch) / 40));
        var cxImg = imgW / 2, cyImg = imgH / 2;
        var maxDist = Math.sqrt(cxImg * cxImg + cyImg * cyImg) || 1;

        // Pass 1 — plain energy score for every candidate window.
        var cands = [], peak = 0, py, px;
        for (py = 0; py <= imgH - ch; py += stepY) {
            for (px = 0; px <= imgW - cw; px += stepX) {
                var gx0 = clamp(Math.floor(px / sal.sx), 0, gw), gy0 = clamp(Math.floor(py / sal.sy), 0, gh);
                var gx1 = clamp(Math.ceil((px + cw) / sal.sx), gx0 + 1, gw), gy1 = clamp(Math.ceil((py + ch) / sal.sy), gy0 + 1, gh);
                var s = areaSum(gx0, gy0, gx1, gy1) / ((gx1 - gx0) * (gy1 - gy0));
                // gentle centre bias — breaks ties towards a natural framing
                var dx = (px + cw / 2) - cxImg, dy = (py + ch / 2) - cyImg;
                s *= (1 - 0.18 * Math.sqrt(dx * dx + dy * dy) / maxDist);
                if (s > peak) peak = s;
                cands.push({ x: px, y: py, s: s });
            }
        }

        // Pass 2 — boost regions applied ADDITIVELY, scaled by the best energy
        // seen. A multiplicative bonus collapses to nothing over a flat
        // backdrop, which is precisely where keeping the face in frame matters
        // most (portrait against a plain wall).
        var unit = peak > 0 ? peak : 1;
        var bestScore = -Infinity, best = { x: base.x, y: base.y };
        for (var i = 0; i < cands.length; i++) {
            var cd = cands[i], score = cd.s;
            for (var b = 0; b < boost.length; b++) {
                var r = boost[b], wgt = (r.weight == null ? 1 : r.weight);
                var inside = r.x >= cd.x && r.y >= cd.y &&
                             r.x + r.w <= cd.x + cw && r.y + r.h <= cd.y + ch;
                var overlaps = r.x < cd.x + cw && r.x + r.w > cd.x &&
                               r.y < cd.y + ch && r.y + r.h > cd.y;
                if (inside) score += wgt * unit;
                else if (overlaps) score -= 0.5 * wgt * unit;   // slicing a face is the worst outcome
                else score -= 0.2 * wgt * unit;                 // losing it entirely is merely bad
            }
            if (score > bestScore) { bestScore = score; best = { x: cd.x, y: cd.y }; }
        }
        return { x: best.x, y: best.y, w: cw, h: ch, score: bestScore };
    }

    /* ════════════════════════════════════════════════════════════════
       SPLIT INTO A GRID
       ════════════════════════════════════════════════════════════════ */

    /**
     * Cut w x h into cols x rows tiles.
     *
     * The remainder is DISTRIBUTED, not dropped: 1000 px into 3 columns gives
     * 334/333/333, never 333/333/333 with a lost pixel column. Tools that floor
     * every tile quietly shave a stripe off the right and bottom of the photo.
     *
     * @param opts.overlap  px each tile extends into its neighbours (poster glue)
     * @returns [{col,row,index,x,y,w,h}] in raster order
     */
    function splitGrid(w, h, cols, rows, opts) {
        opts = opts || {};
        cols = Math.max(1, Math.floor(cols));
        rows = Math.max(1, Math.floor(rows));
        if (cols > w) cols = w;
        if (rows > h) rows = h;
        var overlap = Math.max(0, Math.floor(opts.overlap || 0));

        function edges(total, n) {
            var out = [0], baseSize = Math.floor(total / n), rem = total - baseSize * n;
            for (var i = 0; i < n; i++) out.push(out[i] + baseSize + (i < rem ? 1 : 0));
            return out;
        }
        var xs = edges(w, cols), ys = edges(h, rows);

        var tiles = [];
        for (var r = 0; r < rows; r++) {
            for (var c = 0; c < cols; c++) {
                var x0 = xs[c], x1 = xs[c + 1], y0 = ys[r], y1 = ys[r + 1];
                if (overlap) {
                    x0 = Math.max(0, x0 - (c > 0 ? overlap : 0));
                    y0 = Math.max(0, y0 - (r > 0 ? overlap : 0));
                    x1 = Math.min(w, x1 + (c < cols - 1 ? overlap : 0));
                    y1 = Math.min(h, y1 + (r < rows - 1 ? overlap : 0));
                }
                tiles.push({
                    col: c, row: r, index: r * cols + c,
                    x: x0, y: y0, w: x1 - x0, h: y1 - y0
                });
            }
        }
        return tiles;
    }

    /**
     * Order in which tiles must be POSTED so a social grid ends up looking like
     * the original picture.
     *
     * A profile grid shows the newest post first, filling left-to-right and
     * top-to-bottom. So the bottom-right tile has to go up first and the
     * top-left tile last — the exact reverse of reading order. Getting this
     * wrong produces a mirrored, scrambled mosaic, and most splitters just
     * number the files 1..9 in reading order and leave the user to discover it.
     *
     * @returns array of tile indices; position i = the i-th upload
     */
    function socialUploadOrder(cols, rows) {
        var n = cols * rows, out = new Array(n);
        for (var i = 0; i < n; i++) out[i] = n - 1 - i;
        return out;
    }

    /* ════════════════════════════════════════════════════════════════
       POSTER — spread one image over many printed sheets
       ════════════════════════════════════════════════════════════════ */

    var PAGE_SIZES = {
        a4: { w: 210, h: 297 }, a3: { w: 297, h: 420 }, a5: { w: 148, h: 210 },
        letter: { w: 215.9, h: 279.4 }, legal: { w: 215.9, h: 355.6 },
        tabloid: { w: 279.4, h: 431.8 }
    };

    /**
     * Plan a tiled poster.
     *
     * @param opts.page        'a4' | 'a3' | ... or {w,h} in mm
     * @param opts.landscape   rotate the sheet
     * @param opts.marginMm    unprintable/safety margin per side (default 5)
     * @param opts.overlapMm   glue overlap between sheets (default 0)
     * @param opts.targetWidthMm | opts.targetHeightMm | opts.cols  — pick ONE
     *
     * @returns {cols, rows, sheetW, sheetH, printW, printH, posterWmm, posterHmm,
     *           dpi, overlapMm, pages:[{col,row,index,src:{x,y,w,h}, placeMm:{...}}]}
     *
     * `dpi` is the effective print resolution — the number that decides whether
     * the poster looks sharp or like a blown-up thumbnail. It is returned so the
     * UI can warn BEFORE the user prints twelve sheets.
     */
    function posterPlan(imgW, imgH, opts) {
        opts = opts || {};
        var page = typeof opts.page === 'string'
            ? (PAGE_SIZES[opts.page] || PAGE_SIZES.a4)
            : (opts.page || PAGE_SIZES.a4);
        var sheetW = opts.landscape ? page.h : page.w;
        var sheetH = opts.landscape ? page.w : page.h;
        var margin = opts.marginMm == null ? 5 : Math.max(0, opts.marginMm);
        var overlap = Math.max(0, opts.overlapMm || 0);

        var printW = sheetW - 2 * margin;
        var printH = sheetH - 2 * margin;
        if (printW <= 0 || printH <= 0) throw new Error('margins_exceed_page');
        // Each sheet after the first contributes only (printW - overlap) of new
        // poster width, because the overlap strip is duplicated for gluing.
        var stepW = printW - overlap;
        var stepH = printH - overlap;
        if (stepW <= 0 || stepH <= 0) throw new Error('overlap_exceeds_printable_area');

        var posterW, posterH, cols, rows;
        if (opts.cols) {
            cols = Math.max(1, Math.floor(opts.cols));
            posterW = printW + (cols - 1) * stepW;
            posterH = posterW * imgH / imgW;
            rows = Math.max(1, Math.ceil((posterH - printH) / stepH) + 1);
        } else {
            if (opts.targetHeightMm && !opts.targetWidthMm) {
                posterH = opts.targetHeightMm;
                posterW = posterH * imgW / imgH;
            } else {
                posterW = opts.targetWidthMm || 594;      // A2 width by default
                posterH = posterW * imgH / imgW;
            }
            cols = Math.max(1, Math.ceil((posterW - printW) / stepW) + 1);
            rows = Math.max(1, Math.ceil((posterH - printH) / stepH) + 1);
        }
        // recompute the exact poster size the sheets actually cover
        var coverW = printW + (cols - 1) * stepW;
        var coverH = printH + (rows - 1) * stepH;

        var pxPerMm = imgW / posterW;
        var dpi = pxPerMm * MM_PER_INCH;

        var pages = [];
        for (var r = 0; r < rows; r++) {
            for (var c = 0; c < cols; c++) {
                var mmX = c * stepW, mmY = r * stepH;
                var sx = mmX * pxPerMm, sy = mmY * (imgH / posterH);
                var sw = printW * pxPerMm, sh = printH * (imgH / posterH);
                // the last sheet may hang past the image — clamp the source rect
                // and report how much of the sheet is actually covered
                var cw = Math.min(sw, imgW - sx), chh = Math.min(sh, imgH - sy);
                pages.push({
                    col: c, row: r, index: r * cols + c,
                    src: { x: round(sx), y: round(sy), w: Math.max(0, round(cw)), h: Math.max(0, round(chh)) },
                    placeMm: {
                        x: margin, y: margin,
                        w: printW * (cw / sw), h: printH * (chh / sh)
                    },
                    // where this sheet sits within the finished poster
                    posterMm: { x: mmX, y: mmY }
                });
            }
        }
        return {
            cols: cols, rows: rows, sheets: cols * rows,
            sheetW: sheetW, sheetH: sheetH, marginMm: margin, overlapMm: overlap,
            printW: printW, printH: printH,
            posterWmm: posterW, posterHmm: posterH,
            coverWmm: coverW, coverHmm: coverH,
            dpi: dpi,
            pages: pages
        };
    }

    /* ════════════════════════════════════════════════════════════════
       PRESETS
       ════════════════════════════════════════════════════════════════ */

    // Aspect presets worth having: the ones people actually export to.
    var ASPECTS = [
        { id: 'free', ratio: 0 },
        { id: 'original', ratio: -1 },
        { id: '1:1', ratio: 1 },
        { id: '4:5', ratio: 4 / 5 },
        { id: '9:16', ratio: 9 / 16 },
        { id: '16:9', ratio: 16 / 9 },
        { id: '4:3', ratio: 4 / 3 },
        { id: '3:2', ratio: 3 / 2 },
        { id: '2:3', ratio: 2 / 3 },
        { id: '3:4', ratio: 3 / 4 },
        { id: '21:9', ratio: 21 / 9 },
        { id: 'a4p', ratio: 210 / 297 },
        { id: 'a4l', ratio: 297 / 210 }
    ];

    function parseRatio(text) {
        if (!text) return null;
        var m = String(text).trim().match(/^(\d+(?:[.,]\d+)?)\s*[:\/xX×]\s*(\d+(?:[.,]\d+)?)$/);
        if (!m) {
            var num = parseFloat(String(text).replace(',', '.'));
            return isFinite(num) && num > 0 ? num : null;
        }
        var a = parseFloat(m[1].replace(',', '.')), b = parseFloat(m[2].replace(',', '.'));
        if (!(a > 0 && b > 0)) return null;
        return a / b;
    }

    /** Reduce a pixel size to its smallest integer ratio, e.g. 1920x1080 -> 16:9. */
    function simplifyRatio(w, h) {
        function gcd(a, b) { return b ? gcd(b, a % b) : a; }
        var g = gcd(Math.round(w), Math.round(h)) || 1;
        return { w: Math.round(w) / g, h: Math.round(h) / g };
    }

    /* ════════════════════════════════════════════════════════════════
       CARRYING ONE CROP ACROSS PHOTOS (batch + switching files)
       ════════════════════════════════════════════════════════════════ */

    /**
     * Map a crop rectangle from one photo onto another.
     *
     * Scaling x/w by the width ratio and y/h by the height ratio — the obvious
     * implementation — silently DESTROYS the ratio the user locked: a 1:1 frame
     * carried from a 6000x4000 landscape onto a 4000x6000 portrait comes out
     * 2:3. In a batch nobody sees that until the ZIP is open, which makes it a
     * silent quality downgrade rather than a visible mistake.
     *
     * So the two intents are carried differently:
     *   aspect > 0  — the SHAPE is what the user asked for. Keep the ratio
     *                 exactly, keep the relative size (as a fraction of the
     *                 largest such rect that fits) and the relative centre.
     *   aspect <= 0 — free / "same as original": no shape was requested, so
     *                 carry the relative geometry as-is (a free frame stretches
     *                 with the frame, which is what "free" means).
     *
     * @param rect   {x,y,w,h} in the SOURCE photo's working space
     * @param from   {w,h} working size of the photo the rect came from
     * @param to     {w,h} working size of the photo it is going to
     * @param aspect locked ratio (w/h), 0 for free
     * @param minSize minimum side in px
     */
    function mapRectToFile(rect, from, to, aspect, minSize) {
        var min = Math.max(1, minSize || 1);
        if (!from.w || !from.h || !to.w || !to.h) return constrainRect(rect, to.w, to.h, aspect, min);

        if (aspect && aspect > 0) {
            var srcMax = largestRectOfAspect(from.w, from.h, aspect);
            var dstMax = largestRectOfAspect(to.w, to.h, aspect);
            // how much of the available shape the user kept (never grows past 1)
            var fill = srcMax.w ? Math.min(1, rect.w / srcMax.w) : 1;
            var w = Math.max(min, dstMax.w * fill);
            var h = w / aspect;
            // relative centre, so a crop of the left-hand face stays on the left
            var cx = (rect.x + rect.w / 2) / from.w * to.w;
            var cy = (rect.y + rect.h / 2) / from.h * to.h;
            return constrainRect({ x: cx - w / 2, y: cy - h / 2, w: w, h: h }, to.w, to.h, aspect, min);
        }

        return constrainRect({
            x: rect.x / from.w * to.w,
            y: rect.y / from.h * to.h,
            w: rect.w / from.w * to.w,
            h: rect.h / from.h * to.h
        }, to.w, to.h, 0, min);
    }

    return {
        MM_PER_INCH: MM_PER_INCH,
        PAGE_SIZES: PAGE_SIZES,
        ASPECTS: ASPECTS,
        largestRectOfAspect: largestRectOfAspect,
        constrainRect: constrainRect,
        detectBorders: detectBorders,
        saliencyMap: saliencyMap,
        bestCrop: bestCrop,
        splitGrid: splitGrid,
        socialUploadOrder: socialUploadOrder,
        posterPlan: posterPlan,
        parseRatio: parseRatio,
        simplifyRatio: simplifyRatio,
        mapRectToFile: mapRectToFile
    };
});
